import React, { useState, useEffect } from "react";
import { get, last } from "lodash";
import { useSelector } from "react-redux";
import { IconShare } from "@tabler/icons-react";
import Map from "./components/Map";
import Breadcrumbs from "./components/Breadcrumbs";
import Sidebar from "./components/Sidebar";
import ZoneDrawer from "./components/ZoneDrawer";
import NewsletterModal from "./components/NewsletterModal";
import Button from "../../components/ui/Button";
import usePolygons from "./hooks/usePolygons";
import useInteractionCounter from "./hooks/useInteractionCounter";
import { GadmFeature } from "./components/Map/types";
import { AppStore } from "../../redux/store";

const useNavbarHeight = () => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;
    let mutationObserver: MutationObserver | undefined;

    const measure = () => {
      const navbar = document.getElementById("navbar");
      if (navbar) setHeight(navbar.offsetHeight);
    };

    // Inicia observación del navbar cuando existe en el DOM
    const observe = () => {
      const navbar = document.getElementById("navbar");
      if (!navbar) return false;
      measure();
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(navbar);
      return true;
    };

    // Si el navbar aún no existe, espera a que aparezca en el DOM
    if (!observe()) {
      mutationObserver = new MutationObserver(() => {
        if (observe()) mutationObserver?.disconnect();
      });
      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    window.addEventListener("resize", measure);
    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  return height;
};

const HeatMapHome: React.FC = () => {
  const navbarHeight = useNavbarHeight();
  const country = useSelector((store: AppStore) => get(store, "country.country", "sv"));
  const { features, backgroundFeatures, level, loading, data, trail, priceMin, priceMax, setPriceMin, setPriceMax, loadByParent, navigateToLevel } = usePolygons();
  const showNewsletter = useInteractionCounter();

  // El polígono activo del drawer: se actualiza con click en mapa o con breadcrumbs (via trail)
  const [drawerPolygonId, setDrawerPolygonId] = useState<number | null>(null);

  // Cuando el trail cambia (breadcrumbs o navegación), sincroniza el drawer con el último padre
  useEffect(() => {
    const trailId = get(last(trail), "id", null) as number | null;
    setDrawerPolygonId(trailId);
  }, [trail]);

  // Cierra el ZoneDrawer cuando se abre el newsletter para que no compitan
  useEffect(() => {
    if (showNewsletter) setDrawerPolygonId(null);
  }, [showNewsletter]);

  const handleFeatureClick = (feature: GadmFeature) => {
    const featureId = get(feature, "properties.id") as number | undefined;
    if (!featureId) return;

    // Siempre mostrar el detalle del polígono clickeado en el drawer
    setDrawerPolygonId(featureId);

    // loadByParent valida internamente si allowed_to_search permite navegar
    const parentName = get(feature, `properties.NAME_${level}`) as string | undefined;
    loadByParent(featureId, level + 1, parentName);
  };

  return (
    <div className="w-full relative" style={{ height: `calc(100dvh - ${navbarHeight}px)`, maxHeight: `calc(100dvh - ${navbarHeight}px)` }}>
      {/* Mobile: Sidebar arriba (order-1), Breadcrumbs abajo (order-2)
          Web:    Breadcrumbs arriba full-width (order-1), Sidebar abajo izquierda (order-2) */}
      <div className="absolute z-20 top-4 left-0 right-0 md:top-0 flex flex-col pointer-events-none md:h-full">
        <div className="order-2 md:order-1 w-full pointer-events-auto">
          <Breadcrumbs country={country} activeLevel={level} trail={trail} onNavigate={navigateToLevel} />
        </div>
        <Sidebar country={country} data={data} priceMin={priceMin ?? 0} priceMax={priceMax ?? 0} onPriceMinChange={setPriceMin} onPriceMaxChange={setPriceMax} onZoneSelect={(zone) => { setDrawerPolygonId(zone.id); loadByParent(zone.id, level + 1, zone.name); }} />
      </div>
      {/* Botón compartir — siempre visible, arriba-derecha */}
      <div className="absolute z-30 top-4 right-4 pointer-events-auto">
        <Button size="sm" className="!rounded-xl !gap-1" variant="transparent">
          <span className="text-white text-sm flex items-center gap-1">Compartir <IconShare size={16} /></span>
        </Button>
      </div>
      <Map features={features} backgroundFeatures={backgroundFeatures} loading={loading} onFeatureClick={handleFeatureClick} />
      <ZoneDrawer polygonId={drawerPolygonId} onClose={() => setDrawerPolygonId(null)} navbarHeight={navbarHeight} />
      <NewsletterModal country={country} open={showNewsletter} />
    </div>
  );
};

export default HeatMapHome;
